import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { firestore } from '../../firebase/firebaseConfig';
import ShowroomAdress from './ShowroomAddress';
import ShowroomAccessMap from './ShowroomAccessMap';
import {
  DEFAULT_SHOWROOM_ADDRESS,
  DEFAULT_SHOWROOM_MAP_EMBED,
  DEFAULT_SHOWROOM_MESSAGE,
  DEFAULT_SHOWROOM_TITLE,
  SHOWROOM_COLLECTION,
  SHOWROOM_ROOMS_COLLECTION,
  SHOWROOM_SETTINGS_ID,
} from './showroomSettings';

const defaultTopImage = require('../../images/showroom/top/showroom-top.jpg');

const HeaderImageFrame = styled.div`
  width: 100%;
  height: 80vh;
  margin-bottom: 40px;
  position: relative;

  @media (max-width: 768px) {
    height: auto;
    margin-top: 56px;
    padding-top: 56.25%;
  }
`;

const HeaderImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (max-width: 768px) {
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const AddressMapRow = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto 24px;
  padding: 0 20px;
  gap: 8px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: stretch;
    gap: 32px;
  }

  > * {
    flex: 1 1 0;
    min-width: 0;
  }
`;

const Showroom = () => {
  const importAllImages = (requireContext) => {
    return requireContext.keys().map(requireContext);
  };

  const localImages = useMemo(() => {
    const importedImages = importAllImages(
      require.context('../../images/showroom', false, /\.(png|jpe?g|svg)$/)
    );
    return Array.from(new Set(importedImages));
  }, []);

  const [selectedImage, setSelectedImage] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [topImageSrc, setTopImageSrc] = useState('');
  const [title, setTitle] = useState(DEFAULT_SHOWROOM_TITLE);
  const [message, setMessage] = useState(DEFAULT_SHOWROOM_MESSAGE);
  const [rooms, setRooms] = useState([]);

  useEffect(() => {
    const fetchShowroomSettings = async () => {
      try {
        const snap = await getDoc(doc(firestore, SHOWROOM_COLLECTION, SHOWROOM_SETTINGS_ID));
        if (!snap.exists()) {
          setTopImageSrc(defaultTopImage);
          return;
        }

        const data = snap.data();
        const registeredUrl = typeof data.topImageUrl === 'string' ? data.topImageUrl.trim() : '';
        setTopImageSrc(registeredUrl || defaultTopImage);
        if (typeof data.title === 'string' && data.title.trim() !== '') {
          setTitle(data.title);
        }
        if (typeof data.message === 'string' && data.message.trim() !== '') {
          setMessage(data.message);
        }
      } catch (error) {
        console.error('ショールーム設定の取得に失敗:', error);
        setTopImageSrc(defaultTopImage);
      }
    };

    const fetchRooms = async () => {
      try {
        const roomsQuery = query(
          collection(firestore, SHOWROOM_ROOMS_COLLECTION),
          orderBy('order', 'asc')
        );
        const snapshot = await getDocs(roomsQuery);
        setRooms(snapshot.docs.map((roomDoc) => ({ id: roomDoc.id, ...roomDoc.data() })));
      } catch (error) {
        console.error('ショールーム一覧の取得に失敗:', error);
      }
    };

    fetchShowroomSettings();
    fetchRooms();
  }, []);

  useEffect(() => {
    if (selectedImage) {
      setModalVisible(true);
    } else {
      setTimeout(() => {
        setModalVisible(false);
      }, 500);
    }
  }, [selectedImage]);

  const publishedRooms = rooms.filter((room) => room.published !== false);
  const hasRegisteredGallery = publishedRooms.some((room) => Array.isArray(room.images) && room.images.length > 0);

  const styles = {
    headerSection: {
      marginBottom: '60px',
    },
    textContent: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '0 20px',
      textAlign: 'center',
    },
    title: {
      fontSize: '2rem',
      marginBottom: '2rem',
      fontWeight: 'bold',
    },
    description: {
      fontSize: '1rem',
      lineHeight: '1.8',
      textAlign: 'left',
      marginBottom: '40px',
      whiteSpace: 'pre-line',
    },
    roomSection: {
      marginBottom: '80px',
    },
    roomName: {
      fontSize: '1.6rem',
      fontWeight: 'bold',
      textAlign: 'center',
      margin: '40px 0 24px',
    },
    galleryContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '10px',
      padding: '0 20px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      opacity: selectedImage ? 1 : 0,
      visibility: modalVisible ? 'visible' : 'hidden',
      transition: 'opacity 0.5s ease',
    },
    modalImageWrapper: {
      width: '80%',
      height: '80%',
      maxWidth: '1200px',
      maxHeight: '800px',
      overflow: 'hidden',
    },
    modalImage: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
  };

  const displayRooms = rooms.length > 0
    ? publishedRooms
    : [{ id: 'legacy', name: '', order: 1, images: [], address: '', mapEmbedUrl: '' }];

  return (
    <div>
      <section style={styles.headerSection}>
        <HeaderImageFrame>
          {topImageSrc && (
            <HeaderImage
              src={topImageSrc}
              alt="Showroom Header"
            />
          )}
        </HeaderImageFrame>
        <div style={styles.textContent}>
          <h2 style={styles.title}>{title}</h2>
          <p style={styles.description}>{message}</p>
        </div>
      </section>

      {displayRooms.map((room, index) => {
        const registeredImages = Array.isArray(room.images)
          ? room.images.map((image) => image.url).filter(Boolean)
          : [];
        const galleryImages = registeredImages.length > 0
          ? registeredImages
          : (!hasRegisteredGallery && index === 0 ? localImages : []);
        const address = (typeof room.address === 'string' && room.address.trim() !== '')
          ? room.address
          : (index === 0 ? DEFAULT_SHOWROOM_ADDRESS : '');
        const mapEmbedUrl = (typeof room.mapEmbedUrl === 'string' && room.mapEmbedUrl.trim() !== '')
          ? room.mapEmbedUrl
          : (index === 0 ? DEFAULT_SHOWROOM_MAP_EMBED : '');

        return (
          <section key={room.id} style={styles.roomSection}>
            {room.name ? <h3 style={styles.roomName}>{room.name}</h3> : null}
            {galleryImages.length > 0 && (
              <div style={styles.galleryContainer}>
                {galleryImages.map((image, imageIndex) => (
                  <ImageComponent
                    key={`${room.id}-${imageIndex}`}
                    src={image}
                    alt={`${room.name || 'Gallery'} ${imageIndex + 1}`}
                    onClick={() => setSelectedImage(image)}
                  />
                ))}
              </div>
            )}
            <AddressMapRow>
              <ShowroomAdress title="ショールーム住所" address={address} />
              <ShowroomAccessMap embedUrl={mapEmbedUrl} />
            </AddressMapRow>
          </section>
        );
      })}

      <div
        style={styles.modalOverlay}
        onClick={() => setSelectedImage(null)}
      >
        {selectedImage && (
          <div style={styles.modalImageWrapper}>
            <img
              src={selectedImage}
              alt="Enlarged"
              style={styles.modalImage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ImageComponent = ({ src, alt, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getImageSize = () => {
    if (windowWidth <= 767) {
      return '100%';
    } else if (windowWidth <= 1024) {
      return '100%';
    } else {
      return '100%';
    }
  };

  const styles = {
    imageWrapper: {
      width: getImageSize(),
      aspectRatio: '1 / 1',
      overflow: 'hidden',
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.5s ease',
      transform: hovered ? 'scale(1.1)' : 'scale(1)',
      cursor: 'pointer',
    },
  };

  return (
    <div
      style={styles.imageWrapper}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      <img src={src} alt={alt} style={styles.image} />
    </div>
  );
};

export default Showroom;
